import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { CalendarDays, Plus, Save, X, Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";

type HolidayRow = {
  id: string;
  holiday_name: string;
  holiday_date: string;
  region?: string | null;
  is_national: boolean | null;
};

const currentYear = new Date().getFullYear();

const RosterHolidayManagement: React.FC = () => {
  const [holidays, setHolidays] = useState<HolidayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [yearFilter, setYearFilter] = useState<number>(currentYear);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [newForm, setNewForm] = useState<Partial<HolidayRow>>({
    holiday_name: "",
    holiday_date: "",
    region: "",
    is_national: true,
  });

  const [editForm, setEditForm] = useState<Partial<HolidayRow>>({});

  const loadHolidays = async () => {
    setLoading(true);
    const start = `${yearFilter}-01-01`;
    const end = `${yearFilter}-12-31`;

    const { data, error } = await supabase
      .from("nz_holidays")
      .select("*")
      .gte("holiday_date", start)
      .lte("holiday_date", end)
      .order("holiday_date", { ascending: true });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load holidays",
        variant: "destructive",
      });
    } else {
      setHolidays((data || []) as HolidayRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadHolidays();
  }, [yearFilter]);

  const groupedByMonth = useMemo(() => {
    const map: Record<string, HolidayRow[]> = {};
    for (const h of holidays) {
      const monthKey = format(new Date(h.holiday_date + "T00:00:00"), "MMMM");
      if (!map[monthKey]) map[monthKey] = [];
      map[monthKey].push(h);
    }
    return map;
  }, [holidays]);

  const resetNewForm = () => {
    setNewForm({
      holiday_name: "",
      holiday_date: "",
      region: "",
      is_national: true,
    });
  };

  const handleAdd = async () => {
    if (!newForm.holiday_name?.trim() || !newForm.holiday_date) {
      toast({
        title: "Missing info",
        description: "Holiday name and date are required",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("nz_holidays").insert({
      holiday_name: newForm.holiday_name.trim(),
      holiday_date: newForm.holiday_date,
      region: newForm.region?.trim() || null,
      is_national: newForm.is_national ?? true,
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Added",
        description: `Holiday "${newForm.holiday_name}" added`,
      });
      resetNewForm();
      setShowAddForm(false);
      loadHolidays();
    }
    setSaving(false);
  };

  const startEdit = (holiday: HolidayRow) => {
    setEditingId(holiday.id);
    setEditForm({ ...holiday });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editForm.holiday_name?.trim() || !editForm.holiday_date) {
      toast({
        title: "Missing info",
        description: "Holiday name and date are required",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    const { error } = await supabase
      .from("nz_holidays")
      .update({
        holiday_name: editForm.holiday_name.trim(),
        holiday_date: editForm.holiday_date,
        region: editForm.region?.trim() || null,
        is_national: editForm.is_national ?? true,
      })
      .eq("id", editingId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Updated",
        description: "Holiday updated successfully",
      });
      setEditingId(null);
      setEditForm({});
      loadHolidays();
    }
    setSaving(false);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete holiday "${name}"?`)) return;

    const { error } = await supabase.from("nz_holidays").delete().eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Deleted",
        description: `Holiday "${name}" deleted`,
      });
      loadHolidays();
    }
  };

  const years = useMemo(() => {
    const base = currentYear;
    return [base - 1, base, base + 1, base + 2];
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <CalendarDays className="h-6 w-6" />
            Public Holidays
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage NZ public holidays used for roster calculations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="year-filter" className="text-sm whitespace-nowrap">
            Year:
          </Label>
          <select
            id="year-filter"
            value={yearFilter}
            onChange={(e) => setYearFilter(Number(e.target.value))}
            className="border rounded-md px-3 py-2 text-sm bg-background"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <Button
            onClick={() => setShowAddForm((s) => !s)}
            variant={showAddForm ? "secondary" : "default"}
          >
            {showAddForm ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Holiday
              </>
            )}
          </Button>
        </div>
      </div>

      {showAddForm && (
        <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
          <h3 className="font-semibold">New Holiday</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new-name">Name</Label>
              <Input
                id="new-name"
                value={newForm.holiday_name || ""}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, holiday_name: e.target.value }))
                }
                placeholder="e.g. Waitangi Day"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-date">Date</Label>
              <Input
                id="new-date"
                type="date"
                value={newForm.holiday_date || ""}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, holiday_date: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-region">Region (optional)</Label>
              <Input
                id="new-region"
                value={newForm.region || ""}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, region: e.target.value }))
                }
                placeholder="e.g. Auckland"
              />
            </div>
            <div className="flex items-center gap-2 pt-7">
              <Checkbox
                id="new-national"
                checked={!!newForm.is_national}
                onCheckedChange={(checked) =>
                  setNewForm((f) => ({ ...f, is_national: !!checked }))
                }
              />
              <Label htmlFor="new-national" className="cursor-pointer">
                National holiday
              </Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                resetNewForm();
                setShowAddForm(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Holiday"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          Loading holidays...
        </div>
      ) : holidays.length === 0 ? (
        <div className="text-center py-12 border rounded-lg border-dashed">
          <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            No holidays found for {yearFilter}
          </p>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add your first holiday
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByMonth).map(([month, items]) => (
            <div key={month} className="space-y-2">
              <h3 className="font-semibold text-lg border-b pb-2">{month}</h3>
              <div className="space-y-2">
                {items.map((h) => (
                  <div
                    key={h.id}
                    className="border rounded-lg p-3 bg-card hover:bg-accent/30 transition-colors"
                  >
                    {editingId === h.id ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label>Name</Label>
                            <Input
                              value={editForm.holiday_name || ""}
                              onChange={(e) =>
                                setEditForm((f) => ({
                                  ...f,
                                  holiday_name: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="space-y-1">
                            <Label>Date</Label>
                            <Input
                              type="date"
                              value={editForm.holiday_date || ""}
                              onChange={(e) =>
                                setEditForm((f) => ({
                                  ...f,
                                  holiday_date: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="space-y-1">
                            <Label>Region</Label>
                            <Input
                              value={editForm.region || ""}
                              onChange={(e) =>
                                setEditForm((f) => ({
                                  ...f,
                                  region: e.target.value,
                                }))
                              }
                              placeholder="Optional"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-6">
                            <Checkbox
                              id={`edit-national-${h.id}`}
                              checked={!!editForm.is_national}
                              onCheckedChange={(checked) =>
                                setEditForm((f) => ({
                                  ...f,
                                  is_national: !!checked,
                                }))
                              }
                            />
                            <Label
                              htmlFor={`edit-national-${h.id}`}
                              className="cursor-pointer"
                            >
                              National holiday
                            </Label>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelEdit}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={saving}
                          >
                            <Save className="h-4 w-4 mr-1" />
                            {saving ? "Saving..." : "Save"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex flex-col items-center justify-center bg-primary/10 rounded-md px-3 py-1 min-w-[60px]">
                            <span className="text-xs uppercase text-muted-foreground">
                              {format(
                                new Date(h.holiday_date + "T00:00:00"),
                                "EEE"
                              )}
                            </span>
                            <span className="text-lg font-bold leading-tight">
                              {format(
                                new Date(h.holiday_date + "T00:00:00"),
                                "d"
                              )}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {h.holiday_name}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {h.is_national ? (
                                <Badge variant="default" className="text-xs">
                                  National
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">
                                  Regional
                                </Badge>
                              )}
                              {h.region && (
                                <Badge variant="outline" className="text-xs">
                                  {h.region}
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {format(
                                  new Date(h.holiday_date + "T00:00:00"),
                                  "dd MMM yyyy"
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(h)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(h.id, h.holiday_name)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RosterHolidayManagement;
